import type { TreeDto } from '@lib/dto/tree.dto'
import {
  equipmentBoxId,
  equipmentTimerId,
  equipmentWaterSourceId,
  equipmentZoneId,
} from '@lib/dto/irrigation.dto'
import type {
  IrrigationEquipmentDto,
  IrrigationTimerDto,
  WaterSourceDto,
  ZoneDto,
} from '@lib/dto/irrigation.dto'
import type { SiteBoundary } from '@lib/dto/geo.dto'
import type { SiteChangeEntity } from '@lib/enums/site-change.enum'
import {
  asksWhetherChanged,
  changedFields,
  type AnyTrackedField,
  type SiteRecordSnapshot,
} from '@lib/helpers/site-record'

/*
 * "Was it wrong, or did it change?", which the survey asks on a revisit of an
 * edit that could be either (docs/site-visits.md). The rules of which edits
 * ask are lib's, shared with the server, which reads an unanswered one the
 * same way. This builds the snapshots they compare from the records the
 * screen holds, and words the question for each kind of edit.
 */

/** The two answers, as their buttons read: the site changed, or the record was wrong. */
export interface ChangeQuestion {
  changed: string
  wrong: string
}

const ADDED: ChangeQuestion = {
  changed: 'New since last visit',
  wrong: 'Missed last time',
}
const REMOVED: ChangeQuestion = {
  changed: 'Gone',
  wrong: 'Recorded by mistake',
}
const MOVED: ChangeQuestion = {
  changed: 'Moved',
  wrong: 'Was in the wrong place',
}
const CHANGED: ChangeQuestion = { changed: 'Changed', wrong: 'Was wrong' }
const REDRAWN: ChangeQuestion = { changed: 'Changed', wrong: 'Was drawn wrong' }

const PIN_FIELDS: readonly AnyTrackedField[] = ['latitude', 'longitude']

/** The question adding one asks, on a revisit; null when it asks none. */
export function addQuestion(entity: SiteChangeEntity): ChangeQuestion | null {
  return asksWhetherChanged(entity, { action: 'ADDED' }) ? ADDED : null
}

/** The question removing one asks, on a revisit; null when it asks none. */
export function removeQuestion(
  entity: SiteChangeEntity,
): ChangeQuestion | null {
  return asksWhetherChanged(entity, { action: 'REMOVED' }) ? REMOVED : null
}

/** The question moving one's pin asks, on a revisit; null when it asks none. */
export function moveQuestion(entity: SiteChangeEntity): ChangeQuestion | null {
  return asksWhetherChanged(entity, {
    action: 'CHANGED',
    fields: ['latitude', 'longitude'],
  })
    ? MOVED
    : null
}

/**
 * The question an edit asks, on a revisit: none when nothing it changed
 * could be either (a timer renamed, a tree's pin moved), and otherwise worded
 * for what changed.
 */
export function editQuestion<E extends SiteChangeEntity>(
  entity: E,
  before: SiteRecordSnapshot<E>,
  after: SiteRecordSnapshot<E>,
): ChangeQuestion | null {
  const fields = changedFields(entity, before, after)
  if (!asksWhetherChanged(entity, { action: 'CHANGED', fields })) return null
  if (entity === 'BOUNDARY' || entity === 'AREA') return REDRAWN
  if (fields.every((field) => PIN_FIELDS.some((pin) => pin === field))) {
    return MOVED
  }
  if (entity === 'ZONE' && fields.every((field) => field === 'boundary')) {
    return REDRAWN
  }
  return CHANGED
}

/*
 * Snapshots of the screen's records, built as the server builds its own so
 * the two compare the same fields the same way: coordinates and decimals at
 * their column's scale, rings vertex by vertex, head counts in a fixed order.
 */

function fixed(value: number, scale: number): number {
  const factor = 10 ** scale
  return Math.round(value * factor) / factor
}

function coordinate(value: number): number {
  return fixed(value, 6)
}

/** A decimal string at a scale, or null for none (or one not a number). */
function decimal(value: string | null, scale: number): number | null {
  if (value === null || value.trim() === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? fixed(number, scale) : null
}

function ring(
  boundary: SiteBoundary | null,
): SiteRecordSnapshot<'BOUNDARY'>['boundary'] {
  if (boundary === null) return null
  return {
    vertices: boundary.vertices.map((vertex) => ({
      latitude: vertex.latitude,
      longitude: vertex.longitude,
    })),
  }
}

export function boundarySnapshot(
  boundary: SiteBoundary | null,
): SiteRecordSnapshot<'BOUNDARY'> {
  return { boundary: ring(boundary) }
}

export function areaSnapshot(area: {
  name: string
  boundary: SiteBoundary
}): SiteRecordSnapshot<'AREA'> {
  return { name: area.name, boundary: ring(area.boundary) }
}

export function treeSnapshot(tree: TreeDto): SiteRecordSnapshot<'TREE'> {
  return {
    label: tree.label,
    speciesId: tree.speciesId,
    latitude: coordinate(tree.latitude),
    longitude: coordinate(tree.longitude),
    dbhInches: decimal(tree.dbhInches, 2),
  }
}

export function waterSourceSnapshot(
  source: WaterSourceDto,
): SiteRecordSnapshot<'WATER_SOURCE'> {
  return {
    kind: source.kind,
    label: source.label,
    latitude: coordinate(source.latitude),
    longitude: coordinate(source.longitude),
    meterSize: source.kind === 'CITY_METER' ? source.meterSize : null,
    horsepower:
      source.kind === 'WELL' ? decimal(source.horsepower, 2) : null,
  }
}

export function timerSnapshot(
  timer: IrrigationTimerDto,
): SiteRecordSnapshot<'TIMER'> {
  return {
    label: timer.label,
    latitude: coordinate(timer.latitude),
    longitude: coordinate(timer.longitude),
    partUuid: timer.part?.uuid ?? null,
    waterSourceId: timer.waterSourceId,
  }
}

export function zoneSnapshot(zone: ZoneDto): SiteRecordSnapshot<'ZONE'> {
  return {
    timerId: zone.timerId,
    number: zone.number,
    boundary: ring(zone.boundary),
    headCounts: zone.headCounts
      .filter((entry) => entry.count > 0)
      .map((entry) => ({ type: entry.type, count: entry.count }))
      .sort((a, b) => a.type.localeCompare(b.type)),
  }
}

export function equipmentSnapshot(
  equipment: IrrigationEquipmentDto,
): SiteRecordSnapshot<'EQUIPMENT'> {
  const decoder =
    equipment.kind === 'VALVE' || equipment.kind === 'MASTER_VALVE'
      ? equipment.decoder
      : null
  return {
    kind: equipment.kind,
    name: equipment.kind === 'OTHER' ? equipment.name : null,
    latitude: coordinate(equipment.latitude),
    longitude: coordinate(equipment.longitude),
    partUuid: equipment.part?.uuid ?? null,
    decoderUuid: decoder?.uuid ?? null,
    zoneId: equipmentZoneId(equipment),
    timerId: equipmentTimerId(equipment),
    waterSourceId: equipmentWaterSourceId(equipment),
    size: 'size' in equipment ? equipment.size : null,
    backflowType: equipment.kind === 'BACKFLOW' ? equipment.backflowType : null,
    serialNumber: equipment.kind === 'BACKFLOW' ? equipment.serialNumber : null,
    sensorType: equipment.kind === 'SENSOR' ? equipment.sensorType : null,
    wellComponentType:
      equipment.kind === 'WELL_COMPONENT' ? equipment.wellComponentType : null,
    boxId: equipmentBoxId(equipment),
    spliceCount: equipment.kind === 'VALVE_BOX' ? equipment.spliceCount : null,
  }
}
